import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Building2, Shield } from "lucide-react";

const AUTH_BG = "https://static.prod-images.emergentagent.com/jobs/de8b7e50-5120-4fa2-90f1-4b615beda34b/images/9bc911f1bedab780b5b9f3e54a6247da2cde00442cb80da5d0fbac04a8e9a31e.png";

export default function Login() {
  const { t, lang, setLang } = useI18n();
  const { login, switchCompany } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [pickingFor, setPickingFor] = useState(null); // user object when modal open

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      // Require explicit company selection when the user belongs to 2+ companies
      if ((u?.companies?.length || 0) > 1) {
        setPickingFor(u);
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const onPick = async (cid) => {
    try {
      await switchCompany(cid);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* left form */}
      <div className="flex-1 flex flex-col px-6 sm:px-12 md:px-20 py-10 max-w-2xl mx-auto md:mx-0">
        <header className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <div className="font-heading text-2xl font-bold tracking-tighter text-zinc-950">Cornerstone</div>
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-cs-accent" />
          </div>
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="text-xs bg-transparent border-0 cursor-pointer text-zinc-600 focus:outline-none"
            data-testid="login-language-select"
          >
            <option value="pt">PT</option>
            <option value="en">EN</option>
          </select>
        </header>

        <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full">
          <div className="cs-label">{t("by_company")}</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-zinc-950 mt-3" data-testid="login-title">{t("login_title")}</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-sm text-balance">{t("login_subtitle")}</p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div>
              <label className="cs-label block mb-1.5">{t("email")}</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-md bg-white text-sm font-mono text-zinc-900 focus:outline-none focus:ring-2 focus:ring-cs-accent focus:border-cs-accent transition-colors"
                placeholder={t("email_placeholder")}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="cs-label block mb-1.5">{t("password")}</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                className="w-full px-3 py-2.5 border border-zinc-300 rounded-md bg-white text-sm font-mono text-zinc-900 focus:outline-none focus:ring-2 focus:ring-cs-accent focus:border-cs-accent transition-colors"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            {error && <div className="text-sm text-red-600 font-medium border-l-2 border-red-600 pl-3 py-1" data-testid="login-error">{error}</div>}

            <button type="submit" disabled={submitting}
              className="w-full bg-zinc-950 text-white py-2.5 rounded-md text-sm font-semibold tracking-wide hover:bg-zinc-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {submitting ? "..." : t("login_submit")}
            </button>
          </form>

          <p className="mt-8 text-sm text-zinc-600">
            {t("no_account")} <Link to="/register" className="text-cs-accent font-semibold hover:underline" data-testid="register-link">{t("sign_up")}</Link>
          </p>
        </div>
        <footer className="mt-10 cs-label text-zinc-400">Cornerstone v0.1 · © GT Strategy</footer>
      </div>

      {/* right hero image */}
      <div className="hidden md:block flex-1 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${AUTH_BG}')` }} />
        <div className="absolute inset-0 bg-zinc-950/30" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="cs-label !text-white/70">precision-built schedules</div>
          <p className="font-heading text-3xl font-bold mt-3 leading-tight max-w-md">Plan, sync, equalize. From foundation to finish.</p>
        </div>
      </div>

      {pickingFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/60 p-4" data-testid="company-picker-modal">
          <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-md">
            <div className="px-6 pt-6 pb-3">
              <div className="cs-label">{t("multi_company_section")}</div>
              <h2 className="font-heading text-2xl font-bold tracking-tight mt-2">{t("multi_company_pick_title")}</h2>
              <p className="text-xs text-zinc-600 mt-2">{t("multi_company_pick_help")}</p>
            </div>
            <div className="px-6 pb-6 mt-2 space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
              {(pickingFor.companies || []).map((c) => (
                <button
                  key={c.id}
                  onClick={() => onPick(c.id)}
                  className="w-full flex items-center gap-3 px-4 py-3 border border-zinc-200 rounded-md hover:border-cs-accent hover:bg-orange-50/30 transition-colors text-left"
                  data-testid={`picker-company-${c.id}`}
                >
                  {c.role === "admin" ? <Shield size={16} className="text-cs-accent" /> : <Building2 size={16} className="text-zinc-500" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zinc-900 truncate">{c.name}</div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-0.5 uppercase">{c.role}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
